import React, { useEffect, useState } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { I18n } from '@aws-amplify/core';

import { Refetch } from '@/components/ui/Refetch';
import { useBeraJobSummaryResult } from '@/context/BeraJobSummaryResult';
import { useGetStrainIndex } from '@/hooks/useGetStrainIndex';
import { useGetDownloadVideoURL } from '@/hooks/useGetVideoURL';
import { useGetBeraVersionList } from '@/hooks/useGetBeraVersionList';
import { useGetBeraSummaryResultV2 } from '@/hooks/useGetBeraSummaryResultV2';
import { useFinishBeraJobSummaryV2 } from '@/hooks/useFinishBeraJobSummaryV2';
import { VideoPlayer } from '@/components/ui/@DesignSystem/VideoPlayer';
import { useBeraReportsByJobSummary } from '@/components/views/BeraJob/hooks/useBeraReportV2';

import { Footer } from './Footer';
import { Comment } from './Comment';
import { Informations } from './Informations';
import { VersionPicker } from './VersionPicker';
import { TlidAccordion } from './TlidAccordion';
import { AnalyzedActivities } from './AnalyzedActivities';
import { VideoModal } from './AnalyzedActivities/VideoModal';
import { EnsResultAccordion } from './EnsResultAccordion';
import { BeraResultAccordion } from './BeraResultAccordion';
import { ActionPlansAccordion } from './ActionPlansAccordion';
import { useBeraClipStatus, useFinalResultActions, useRetryBeraClip } from './_hooks';
import { findTaskReport, toAnalyzedActivities } from './utils';
import type { AnalyzedActivity } from './utils';
import { BackArrow, Content as ContentCard, NavButton, VideoBox } from './styles';
import { ALL_REPORTS_FILTER, SECTION } from '@/components/views/BeraJob/BeraReportV2/constants';

import type { BeraClipFile, BeraReportV2, BeraTaskResultV2 } from '@/types/Bera';
import type { StrainIndexReport } from '@/types/StrainIndex';

const NO_RPN = 0;
const EMPTY_FILE_ID = '';

interface OpenClip {
	title: string;
	url: string;
}

type ClipEntry = [string, BeraClipFile | null];

type ThumbnailEntry = [string, string | null];

interface ContentProps {
	onBack: () => void;
}

export function Content({ onBack }: Readonly<ContentProps>): JSX.Element {
	const { file, jobSummary, organizationId, companyId } = useBeraJobSummaryResult();

	const [selectedVersionId, setSelectedVersionId] = useState<string>(jobSummary.id);
	const [openTlidId, setOpenTlidId] = useState<string | null>(null);
	const [openSection, setOpenSection] = useState<string | null>(null);
	const [isVideoVisible, setIsVideoVisible] = useState<boolean>(true);
	const [openClip, setOpenClip] = useState<OpenClip | null>(null);
	const [retryingActivityId, setRetryingActivityId] = useState<string | null>(null);

	const finish = useFinishBeraJobSummaryV2();

	const isCurrentVersion = selectedVersionId === jobSummary.id;

	const versions = useGetBeraVersionList({
		id: jobSummary.id,
		organization_id: organizationId,
		company_id: companyId
	});

	const versionList = versions.data ?? [];

	const summary = useGetBeraSummaryResultV2({
		id: selectedVersionId,
		organization_id: organizationId,
		company_id: companyId,
		enabled: finish.isSuccess
	});

	useEffect(() => {
		if (!jobSummary.id || !organizationId || !companyId) {
			return;
		}

		finish.mutate({ id: jobSummary.id, organization_id: organizationId, company_id: companyId });
	}, [jobSummary.id, organizationId, companyId]);

	const reports = useBeraReportsByJobSummary({
		organization_id: organizationId,
		company_id: companyId,
		bera_job_summary_id: selectedVersionId,
		...ALL_REPORTS_FILTER
	});

	useBeraClipStatus({
		organization_id: organizationId,
		company_id: companyId,
		job_summary_id: selectedVersionId
	});

	const selectedFileId = summary.data?.informations.file_id ?? EMPTY_FILE_ID;

	const video = useGetDownloadVideoURL({
		organization_id: organizationId,
		company_id: companyId,
		file_id: selectedFileId,
		original_name: file.original_name
	});

	const strainIndexReport = useGetStrainIndex({
		organization_id: organizationId,
		company_id: companyId,
		file_id: selectedFileId
	});

	const retryClip = useRetryBeraClip();

	const {
		comment,
		isConsolidated,
		isConsolidating,
		isDownloading,
		handleCommentChange,
		handleCommentBlur,
		handleToggleConsolidate,
		handleDownloadPdf
	} = useFinalResultActions({
		organization_id: organizationId,
		company_id: companyId,
		job_summary_id: selectedVersionId,
		summary: summary.data
	});

	const isLoading = finish.isLoading || summary.isLoading || reports.isLoading;

	const isError = finish.isError || summary.isError || reports.isError;

	const hasSummary = !!summary.data?.id;

	const isEns = !!summary.data?.gns;

	const allReports = reports.data?.rows ?? [];

	const videoUrl = video.data;

	const isVideoShown = isVideoVisible && !!videoUrl;

	let strainIndex: StrainIndexReport | undefined;

	if (!strainIndexReport.isError) {
		strainIndex = strainIndexReport.data;
	}

	const tasks = summary.data?.tasks ?? [];

	const analyzedFiles = summary.data?.analyzed_files ?? [];

	const analyzedActivities = toAnalyzedActivities(analyzedFiles, tasks);

	const clipEntries = tasks.map((task): ClipEntry => [task.bera_report_id, task.file]);

	const clipsByReport = new Map(clipEntries);

	const thumbnailEntries = tasks.map((task: BeraTaskResultV2): ThumbnailEntry => [
		task.bera_report_id,
		task.thumbnail_url
	]);

	const thumbnailsByReport = new Map(thumbnailEntries);

	const foundReports = tasks.map((task) => findTaskReport(task, allReports));

	const tlidReports = foundReports.filter((report): report is BeraReportV2 => !!report);

	const isCommentDisabled = isConsolidated || !isCurrentVersion;

	function handleRetrySettled(): void {
		setRetryingActivityId(null);
	}

	function handleRetryClip(activity: AnalyzedActivity): void {
		const params = {
			id: activity.id,
			organization_id: organizationId,
			company_id: companyId,
			job_summary_id: jobSummary.id
		};

		setRetryingActivityId(activity.id);
		retryClip.mutate(params, { onSettled: handleRetrySettled });
	}

	function handleRefetch(): void {
		if (!finish.isSuccess) {
			finish.mutate({ id: jobSummary.id, organization_id: organizationId, company_id: companyId });
			return;
		}

		summary.refetch();
		reports.refetch();
	}

	function handleVersionChange(versionId: string): void {
		setSelectedVersionId(versionId);
	}

	function handleToggleTlid(reportId: string): void {
		setOpenTlidId((current) => (current === reportId ? null : reportId));
	}

	function handleToggleBera(): void {
		setOpenSection((current) => (current === SECTION.BERA ? null : SECTION.BERA));
	}

	function handleToggleEns(): void {
		setOpenSection((current) => (current === SECTION.ENS ? null : SECTION.ENS));
	}

	function handleToggleActionPlans(): void {
		setOpenSection((current) => (current === SECTION.ACTION_PLANS ? null : SECTION.ACTION_PLANS));
	}

	function handleCloseVideo(): void {
		setIsVideoVisible(false);
	}

	function handleOpenClip(clip: BeraClipFile): void {
		if (!clip.url) {
			return;
		}

		setOpenClip({ title: clip.original_name, url: clip.url });
	}

	function handleCloseClip(): void {
		setOpenClip(null);
	}

	if (isLoading) {
		return <Skeleton active />;
	}

	if (isError || !hasSummary) {
		return <Refetch onClick={handleRefetch} />;
	}

	return (
		<Row gutter={[0, 32]}>
			{!!versionList.length && (
				<Col span={24}>
					<VersionPicker value={selectedVersionId} versions={versionList} onChange={handleVersionChange} />
				</Col>
			)}
			<Col span={24}>
				<ContentCard>
					<Row gutter={[0, 40]}>
						<Col span={24}>
							<Informations informations={summary.data.informations} isEns={isEns} />
						</Col>
						{isVideoShown && (
							<Col span={24}>
								<VideoBox>
									<VideoPlayer
										showVolume={false}
										showMinimize
										showCloseButton
										src={videoUrl}
										onClose={handleCloseVideo}
									/>
								</VideoBox>
							</Col>
						)}
						<Col span={24}>
							<AnalyzedActivities
								activities={analyzedActivities}
								retryingActivityId={retryingActivityId}
								onRetry={handleRetryClip}
							/>
						</Col>
						<Col span={24}>
							<Row gutter={[0, 24]}>
								{tlidReports.map((report) => (
									<Col key={report.id} span={24}>
										<TlidAccordion
											report={report}
											open={openTlidId === report.id}
											onToggle={handleToggleTlid}
											clip={clipsByReport.get(report.id)}
											thumbnailUrl={thumbnailsByReport.get(report.id)}
											strainIndex={strainIndex}
											onOpenClip={handleOpenClip}
										/>
									</Col>
								))}
							</Row>
						</Col>
						<Col span={24}>
							<BeraResultAccordion
								cycleSummary={summary.data.cycle_summary}
								highestRpnScores={summary.data.highest_rpn_scores}
								open={openSection === SECTION.BERA}
								onToggle={handleToggleBera}
							/>
						</Col>
						<Col span={24}>
							<EnsResultAccordion
								cycleSummary={summary.data.cycle_summary}
								isEns={isEns}
								jobSummaryId={selectedVersionId}
								open={openSection === SECTION.ENS}
								onToggle={handleToggleEns}
							/>
						</Col>
						<Col span={24}>
							<ActionPlansAccordion
								key={selectedVersionId}
								jobSummaryId={selectedVersionId}
								fileId={summary.data.informations.file_id}
								workstationId={summary.data.informations.workstation_id}
								rpn={summary.data.informations.overall_score ?? NO_RPN}
								open={openSection === SECTION.ACTION_PLANS}
								onToggle={handleToggleActionPlans}
							/>
						</Col>
						<Col span={24}>
							<Comment
								value={comment}
								disabled={isCommentDisabled}
								onChange={handleCommentChange}
								onBlur={handleCommentBlur}
							/>
						</Col>
						<Col span={24}>
							<Footer
								readOnly={!isCurrentVersion}
								consolidated={isConsolidated}
								consolidating={isConsolidating}
								downloading={isDownloading}
								onToggleConsolidate={handleToggleConsolidate}
								onDownload={handleDownloadPdf}
							/>
						</Col>
					</Row>
				</ContentCard>
			</Col>
			<Col span={24}>
				<Row justify="center">
					<Col>
						<NavButton icon={<BackArrow />} aria-label={I18n.get('Previous')} onClick={onBack} />
					</Col>
				</Row>
			</Col>
			{!!openClip && <VideoModal title={openClip.title} url={openClip.url} onClose={handleCloseClip} />}
		</Row>
	);
}
